<template>
  <BaseModal
    :modalTitle="modalTitle"
    contentPadding="none">
    <div
      v-if="isLoading"
      class="flex items-center justify-center gap-2 px-6 py-10 text-sm text-text-muted">
      <IconLucideLoader2 class="h-4 w-4 animate-spin" />
      <span>{{ $t('W: Henter bilaget') }}</span>
    </div>

    <p
      v-else-if="errorText"
      class="px-6 py-10 text-center text-sm text-feedback-error"
      data-testid="bilag-preview-error">
      {{ errorText }}
    </p>

    <template v-else>
      <!-- The same bar as the report previews: the download, saved under the Bilag's current Vist navn. -->
      <div class="flex items-center justify-end gap-3 border-b border-border-subtle px-6 py-3">
        <UiBaseButton
          :text="$t('W: Download PDF')"
          data-testid="bilag-preview-download"
          @click="onDownloadClick">
          <template #icon-left>
            <IconLucideDownload />
          </template>
        </UiBaseButton>
      </div>

      <!-- Same frame as the report previews: the pages on the sunken background, centred at a fixed width. -->
      <div class="flex justify-center bg-surface-sunken px-6 pt-4 pb-6">
        <div
          class="flex flex-col gap-4"
          :style="{ width: `${PAGE_WIDTH_PX}px` }"
          data-testid="bilag-preview-pages">
          <canvas
            v-for="pageNumber in pageCount"
            :key="pageNumber"
            ref="pageCanvases"
            :data-page="pageNumber"
            class="bg-white shadow-sm"
            :aria-label="$t('W: Side {page} af {count}', { page: pageNumber, count: pageCount })"
            data-testid="bilag-preview-page" />
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { markRaw } from 'vue'
import { mapState } from 'pinia'
import { getBlob, ref as storageRef } from 'firebase/storage'
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist'
import { sanitizeFilenameSegment } from '@team-milva/lab-utils/filename'
import { storage } from '@/firebase/init'
import { useBilagLibraryItemStore } from '@/stores/bilagLibraryItemStore'
import { downloadBlob } from '@/globals/typescript/utils/downloadBlob'
import { loadPdfJs } from '@/globals/typescript/utils/loadPdfJs'
import { reportUnexpectedError } from '@/globals/typescript/utils/reportError'
import { useToast } from '@/globals/typescript/utils/toast'

/** The width a page is drawn at — an A4 page at 80 %, like the report previews. */
const PAGE_WIDTH_PX = 684

/**
 * Previews a Bilag library item's PDF in the app and downloads it under its Vist navn.
 *
 * The file is read once from Storage and the same blob is drawn (PDF.js, loaded only when this
 * opens) and saved, so the download is the file as shown. Opening the raw Storage URL in a tab
 * instead made the browser's own Save name the file after the Storage object — the Firestore ID.
 * The name is built when Download is clicked, from the item as it is then (see
 * `downloadStorageFile.ts` for why a name is never baked in earlier).
 */
export default {
  name: 'BilagPreviewModal',

  props: {
    bilagLibraryItemID: { type: String, required: true },
  },

  data() {
    return {
      PAGE_WIDTH_PX,
      isLoading: true,
      errorText: '',
      pageCount: 0,
      blob: null as Blob | null,
      // PDF.js manages its own internal state — a Proxy around it would make every internal
      // mutation reactive (see `markRaw` in the repo's CLAUDE.md).
      loadingTask: null as PDFDocumentLoadingTask | null,
      renderTask: null as RenderTask | null,
      isClosed: false,
    }
  },

  computed: {
    ...mapState(useBilagLibraryItemStore, ['allBilagLibraryItems']),

    item() {
      return this.allBilagLibraryItems.find((x) => x.id === this.bilagLibraryItemID)
    },

    modalTitle(): string {
      return this.item
        ? this.$t('W: Forhåndsvisning af {name}', { name: this.item.displayTitle })
        : this.$t('W: Forhåndsvisning')
    },
  },

  methods: {
    async load(): Promise<void> {
      const storagePath = this.item?.storagePath
      if (!storagePath) {
        this.fail()
        return
      }

      try {
        const [pdfJs, blob] = await Promise.all([
          loadPdfJs(),
          getBlob(storageRef(storage, storagePath)),
        ])
        if (this.isClosed) return

        // Kept, so closing the modal while the file is still being opened can stop it.
        this.loadingTask = markRaw(pdfJs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }))
        if (this.isClosed) {
          void this.loadingTask.destroy()
          return
        }
        const pdfDocument = markRaw(await this.loadingTask.promise)
        if (this.isClosed) return

        this.blob = blob
        this.pageCount = pdfDocument.numPages
        this.isLoading = false
        // The canvases exist once the page count has rendered.
        await this.$nextTick()
        await this.renderPages(pdfDocument)
      } catch (error) {
        if (this.isClosed) return
        // The read goes straight to Storage and the drawing happens here, so no server has seen a
        // denial, a network failure or a file that is not a PDF.
        reportUnexpectedError(error, {
          operation: 'bilag-preview',
          extra: { bilagLibraryItemID: this.bilagLibraryItemID, storagePath },
        })
        this.fail()
      } finally {
        this.isLoading = false
      }
    },

    fail(): void {
      // Never leave the spinner up for good, whichever path got here.
      this.isLoading = false
      this.errorText = this.$t('W: Bilaget kunne ikke vises')
      useToast().error(this.errorText)
    },

    /** Draws the pages one after another, each sharp on a high-density screen. */
    async renderPages(pdfDocument: PDFDocumentProxy): Promise<void> {
      const canvases = (this.$refs.pageCanvases ?? []) as HTMLCanvasElement[]
      const pixelRatio = window.devicePixelRatio || 1

      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
        const page = await pdfDocument.getPage(pageNumber)
        if (this.isClosed) return
        const canvas = canvases.find((x) => x.dataset.page === String(pageNumber))
        if (!canvas) continue

        const fitScale = PAGE_WIDTH_PX / page.getViewport({ scale: 1 }).width
        const viewport = page.getViewport({ scale: fitScale * pixelRatio })
        canvas.width = Math.floor(viewport.width)
        canvas.height = Math.floor(viewport.height)
        canvas.style.width = `${PAGE_WIDTH_PX}px`
        canvas.style.height = `${Math.floor(viewport.height / pixelRatio)}px`

        this.renderTask = markRaw(page.render({ canvas, viewport }))
        await this.renderTask.promise
      }
    },

    onDownloadClick(): void {
      if (!this.blob) return
      const name = sanitizeFilenameSegment(this.item?.displayTitle ?? '') || 'Bilag'
      downloadBlob(this.blob, `${name}.pdf`)
      useToast().success(this.$t('W: PDF downloadet'))
    },
  },

  created() {
    void this.load()
  },

  beforeUnmount() {
    this.isClosed = true
    this.renderTask?.cancel()
    // Frees the document and its worker; also ends an open that is still in progress.
    void this.loadingTask?.destroy()
  },
}
</script>
